/**
 * MobileLauncher (Jitterbug Phase 2a)
 * ------------------------------------
 * Presentational launcher screen. Mounted at /home OUTSIDE AppShell — see
 * App.jsx isHomeRoute branch, which mirrors the /supplier/* chrome-bypass
 * pattern (ProtectedRoute, no AppShell wrapper).
 *
 * Reachable only by direct URL today; no auto-redirect, no DefaultRedirect
 * change. The trigger that decides who lands here is Phase 2b.
 *
 * Tile model (locked in chat 2026-06-01):
 *   - HERO: full-width Asset Registry tile (/registry), pulled out and
 *     de-duplicated from the section groups below.
 *   - Then, in rail order (digitization → disposal → lifecycle → moves),
 *     for each non-admin section:
 *       * directNavigate section (no items): one full-width tile.
 *       * section with items: a group header + 2-col grid of item tiles,
 *         excluding any tile whose route === HERO_ROUTE.
 *     Field Inventory appears under both Disposal Review and Lifecycle
 *     Review and, since the S5.7 repoint, BOTH point at /field — two tiles
 *     still render under two section headers, and the React keys stay
 *     unique (disposal:/field, lifecycle:/field).
 *   - Admin section last (only present for admins per the selector).
 *
 * No API imports. Role comes from useUser; visibility comes from the
 * shared getVisibleSections selector — same source of truth as AppShell.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, ClipboardCheck, ListChecks, LayoutDashboard,
  PackageCheck, Truck, ListOrdered, Users, Building2, FolderKanban,
  Wrench, Download, Trash2, BarChart3, Box, Smartphone,
} from 'lucide-react';
import { useUser } from '../../shared/context/UserContext';
import { getVisibleSections } from '../../shared/nav/sections';
import InstallPrompt from './InstallPrompt';
import './MobileLauncher.css';

const HERO_ROUTE = '/registry';

/* Route → lucide-react icon component. Fallback Box for any unmapped route. */
const ROUTE_ICONS = {
  '/registry':              ClipboardList,
  '/digitization':          ClipboardCheck,
  '/disposal/recon':        ListChecks,
  '/disposal/portal':       LayoutDashboard,
  '/supplier/disposal':     PackageCheck,
  '/lifecycle/recon':       ListChecks,
  '/moves':                 Truck,
  '/field':                 Smartphone,
  '/admin/list-assignment': ListOrdered,
  '/admin/user-management': Users,
  '/admin/site-management': Building2,
  '/admin/projects':        FolderKanban,
  '/admin/data-ops':        Wrench,
  '/admin/data':            Download,
  '/admin/disposal':        Trash2,
  '/admin/dispositions':    BarChart3,
};

function iconFor(route) {
  return ROUTE_ICONS[route] || Box;
}

export default function MobileLauncher() {
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  const visibleSections = getVisibleSections({ role });

  // HERO: first Asset Registry item we find across all visible sections.
  // If the user role somehow has no /registry visibility, fall back to a
  // labeled hero pointing at the same route — the route guard handles auth.
  const heroItem =
    visibleSections.flatMap(s => s.items || []).find(i => i.route === HERO_ROUTE)
    || { label: 'Asset Registry', route: HERO_ROUTE };

  const nonAdminSections = visibleSections.filter(s => !s.adminOnly);
  const adminSection = visibleSections.find(s => s.adminOnly);

  const HeroIcon = iconFor(heroItem.route);

  function go(route) {
    navigate(route);
  }

  return (
    <div className="launcher-screen">
      <header className="launcher-header">
        <div className="launcher-logo">
          <svg width="36" height="36" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <polygon points="30,3 53,16 53,44 30,57 7,44 7,16" stroke="currentColor" strokeWidth="3"/>
            <circle cx="30" cy="30" r="11" stroke="currentColor" strokeWidth="2.2"/>
            <circle cx="30" cy="30" r="4" fill="currentColor"/>
          </svg>
          <span className="launcher-logo-text">ToolSight</span>
        </div>
      </header>

      <InstallPrompt />

      <button
        type="button"
        className="launcher-tile launcher-tile-hero"
        onClick={() => go(heroItem.route)}
      >
        <HeroIcon size={56} strokeWidth={1.5} className="launcher-tile-icon" aria-hidden="true" />
        <span className="launcher-tile-label">{heroItem.label}</span>
      </button>

      {nonAdminSections.map(section => {
        // directNavigate OR launcherSingleTile → one full-width tile (identical markup).
        // launcherSingleTile must win over the items branch below so a flyout-on-rail
        // section (e.g. Digitization) still renders as a single launcher tile.
        if (section.directNavigate || section.launcherSingleTile) {
          const Icon = iconFor(section.route);
          return (
            <button
              key={section.key}
              type="button"
              className="launcher-tile launcher-tile-full"
              onClick={() => go(section.route)}
            >
              <Icon size={44} strokeWidth={1.5} className="launcher-tile-icon" aria-hidden="true" />
              <span className="launcher-tile-label">{section.label}</span>
            </button>
          );
        }
        const items = (section.items || []).filter(i => i.route !== HERO_ROUTE);
        if (items.length === 0) return null;
        return (
          <section key={section.key} className="launcher-group">
            <h2 className="launcher-group-header">{section.label}</h2>
            <div className="launcher-grid">
              {items.map(item => {
                const Icon = iconFor(item.route);
                return (
                  <button
                    key={`${section.key}:${item.route}`}
                    type="button"
                    className="launcher-tile"
                    onClick={() => go(item.route)}
                  >
                    <Icon size={36} strokeWidth={1.5} className="launcher-tile-icon" aria-hidden="true" />
                    <span className="launcher-tile-label">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {adminSection && (
        <section className="launcher-group">
          <h2 className="launcher-group-header">{adminSection.label}</h2>
          <div className="launcher-grid">
            {(adminSection.items || []).map(item => {
              const Icon = iconFor(item.route);
              return (
                <button
                  key={`admin:${item.route}`}
                  type="button"
                  className="launcher-tile"
                  onClick={() => go(item.route)}
                >
                  <Icon size={36} strokeWidth={1.5} className="launcher-tile-icon" aria-hidden="true" />
                  <span className="launcher-tile-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
